import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL, apiRequest } from "@/utils/apiClient";
import { useAuth } from "@/state/auth";

export interface DocumentRecord {
  id: string;
  filename: string;
  extension: string;
  size_bytes: number;
  context: string | null;
  uploaded_by: string | null;
  created_at: string;
  visibility_roles: string[];
  editable?: boolean;
}

export interface TextDocumentPayload {
  title: string;
  text: string;
  context: string | null;
  visibilityRoles: string[];
}

export const useDocuments = () => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: ["admin", "documents"],
    queryFn: async () => {
      const { documents } = await apiRequest<{ documents: DocumentRecord[] }>("/api/documents");
      return documents;
    },
    enabled: Boolean(profile),
  });
};

export const useUploadDocument = () => {
  const profile = useAuth((state) => state.profile);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, context, visibilityRoles }: { file: File; context: string | null; visibilityRoles: string[] }) => {
      const formData = new FormData();
      formData.append("file", file);
      if (context) formData.append("context", context);
      if (visibilityRoles?.length) {
        formData.append("visibility_roles", JSON.stringify(visibilityRoles));
      }
      const response = await fetch(new URL("/api/documents", API_BASE_URL), {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || "Upload failed");
      }
      return response.json() as Promise<{ document: DocumentRecord }>;
    },
    onSuccess: () => {
      if (profile) {
        queryClient.invalidateQueries({ queryKey: ["admin", "documents"] });
      }
    },
  });
};

export const useCreateTextDocument = () => {
  const profile = useAuth((state) => state.profile);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: TextDocumentPayload) => {
      const body = {
        title: payload.title,
        text: payload.text,
        context: payload.context,
        visibility_roles: payload.visibilityRoles,
      };
      const { document } = await apiRequest<{ document: DocumentRecord }>(
        "/api/documents/text",
        { method: "POST", body: JSON.stringify(body) }
      );
      return document;
    },
    onSuccess: () => {
      if (profile) {
        queryClient.invalidateQueries({ queryKey: ["admin", "documents"] });
      }
    },
  });
};

export const useDocumentText = (documentId: string | null) => {
  const profile = useAuth((state) => state.profile);
  return useQuery({
    queryKey: ["admin", "document-text", documentId],
    queryFn: async () => {
      if (!documentId) return null;
      const { document, text } = await apiRequest<{ document: DocumentRecord; text: string }>(
        `/api/documents/${documentId}/text`
      );
      return { document, text };
    },
    enabled: Boolean(profile && documentId),
    staleTime: 0,
  });
};

export const useUpdateTextDocument = () => {
  const profile = useAuth((state) => state.profile);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: TextDocumentPayload & { id: string }) => {
      const body = {
        title: payload.title,
        text: payload.text,
        context: payload.context,
        visibility_roles: payload.visibilityRoles,
      };
      const { document } = await apiRequest<{ document: DocumentRecord }>(
        `/api/documents/${id}`,
        { method: "PUT", body: JSON.stringify(body) }
      );
      return document;
    },
    onSuccess: (_doc, variables) => {
      if (profile) {
        queryClient.invalidateQueries({ queryKey: ["admin", "documents"] });
        queryClient.invalidateQueries({
          queryKey: ["admin", "document-text", variables.id],
        });
      }
    },
  });
};

export const useDeleteDocument = () => {
  const profile = useAuth((state) => state.profile);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (documentId: string) => {
      await apiRequest<void>(`/api/documents/${documentId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      if (profile) {
        queryClient.invalidateQueries({ queryKey: ["admin", "documents"] });
      }
    },
  });
};
